import { useEffect, useState } from "react";

/**
 * Returns true when the current URL contains `?show=yes` (or `&show=yes`).
 * Reactive — updates when the browser navigates (SPA route change or
 * back/forward). Used to surface the underlying DB field name next to every
 * form input in the property admin so devs can trace values quickly.
 */
export function useShowDbFieldNames() {
  const compute = () =>
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("show") === "yes";
  const [enabled, setEnabled] = useState(compute);
  useEffect(() => {
    const onNav = () => setEnabled(compute());
    window.addEventListener("popstate", onNav);
    window.addEventListener("hashchange", onNav);
    return () => {
      window.removeEventListener("popstate", onNav);
      window.removeEventListener("hashchange", onNav);
    };
  }, []);
  return enabled;
}

/**
 * Small inline badge used to render `name` next to a field label. Renders
 * nothing when `enabled` is false, keeping production output clean.
 */
export function DbFieldBadge({ name, enabled }) {
  if (!enabled || !name) return null;
  return (
    <span
      className="ml-2 inline-block text-[10px] font-mono text-blue-700 bg-blue-50 border border-blue-200 rounded px-1 align-middle"
      data-testid={`db-field-badge-${name}`}
      title="Database field name (visible because ?show=yes)"
    >
      {name}
    </span>
  );
}
